import React, { ReactNode } from 'react';
import { JRCH3 } from '../JRCTypo/JRCTypo';
import { JRCIconButton } from '../JRCIconButton/JRCIconButton';
import ReactDOM from 'react-dom';
import {
    JRCModalBox,
    JRCModalClose,
    JRCModalContent,
    JRCModalFooter,
    JRCModalHeader,
    JRCModalTitle,
} from './JRCSidePanelModal.styles';
import { ClickAwayListener } from 'components/Form/Common/ClickAwayListener';

/**
 * Type for JRCSidePanelModal props
 */
export type JRCSidePanelModalProps = {
    open: boolean;
    closeHandler: () => void;
    children?: ReactNode;
    title?: React.ReactNode;
    showIconClose?: boolean;
    enableClickAwayCloseModal?: boolean;
    position: 'left' | 'right';
    footer?: ReactNode;
    removePadding?: boolean;
    inplace?: boolean;
    offsetTop?: number;
};

const ModalBoxElement = (
    {
        closeHandler,
        children,
        title,
        showIconClose,
        enableClickAwayCloseModal,
        footer,
        removePadding,
        position,
        open,
        inplace,
        offsetTop,
    }: JRCSidePanelModalProps,
    ref: React.ForwardedRef<HTMLDivElement>,
) => {
    const [internalOpen, setInternalOpen] = React.useState(false);

    React.useEffect(() => {
        const intervalId = setInterval(() => {
            setInternalOpen(open);
        }, 5);
        return () => {
            clearInterval(intervalId);
        };
    }, [open]);

    const closeModal = () => {
        closeHandler();
    };

    const handleClickAway = (event: any) => {
        event.stopPropagation();
        closeHandler();
    };

    return (
        <ClickAwayListener
            onClickAway={
                enableClickAwayCloseModal
                    ? handleClickAway
                    : () => {
                          // Nothing to do
                      }
            }>
            <JRCModalBox ref={ref} position={position} open={internalOpen} inplace={inplace} offsetTop={offsetTop}>
                {(title || showIconClose) && (
                    <JRCModalHeader className={'modal-header'} removePadding={removePadding}>
                        <ModalTitle>{title}</ModalTitle>
                        {showIconClose && (
                            <JRCModalClose>
                                <JRCIconButton icon="icon-times" onClick={closeModal} />
                            </JRCModalClose>
                        )}
                    </JRCModalHeader>
                )}
                <JRCModalContent className="modal-content" removePadding={removePadding}>
                    {children}
                </JRCModalContent>
                {footer && <JRCModalFooter>{footer}</JRCModalFooter>}
            </JRCModalBox>
        </ClickAwayListener>
    );
};
const ModalBox = React.forwardRef(ModalBoxElement);

const ModalTitle = ({ children }: { children?: React.ReactNode }) => {
    return children ? (
        <JRCModalTitle>
            <JRCH3>{children}</JRCH3>
        </JRCModalTitle>
    ) : (
        <></>
    );
};

const SidePanelModal = ({ open, ...props }: JRCSidePanelModalProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    const [openCondition, setOpenCondition] = React.useState(open);

    React.useEffect(() => {
        let timeoutId: number | undefined = undefined;
        if (!open) {
            timeoutId = window.setTimeout(() => {
                setOpenCondition(open);
            }, 500);
        } else {
            setOpenCondition(open);
        }
        return () => {
            timeoutId && clearTimeout(timeoutId);
        };
    }, [open]);

    const portalDiv = document.getElementById('jrc-container-panel');
    if (openCondition) {
        if (portalDiv) {
            return ReactDOM.createPortal(<ModalBox {...props} ref={ref} open={open} />, portalDiv);
        }
        return <ModalBox {...props} ref={ref} open={open} />;
    }
    return null;
};

/**
 * ℹ️⚠️
 * Used as a child component of {@link JRCContainer}, the modal will be mounted in a preset portal container.
 * Otherwise :
 *   - The modal will be mounted where it is declared (no portal).
 *   - The modal can be mounted in a specific portal container provided manually with the 'jrc-container-panel' id.
 */
export const JRCSidePanelModal = React.forwardRef(SidePanelModal);
